import { MatchPhone } from "refsix-js-models";
import { copyObject } from "refsix-core";
import { store } from "../redux/store";
import request from "./request";
import type { MatchRibbonsPublic } from "../pages/venue/ribbonTypes";
import { isEligible } from "./leaderboardOptInService";
import { invalidateVenue } from "../components/venue/venueStore";
import { updateMatch } from "./matchService";

/**
 * The only place the phone talks to the SCORZA-739 venue leaderboard endpoints.
 *
 * The shapes below are the frozen public HTTP contract (VENUE-CONTRACT Appendix C) — they are
 * deliberately NOT the server's internal documents. In particular no `username` ever crosses
 * this boundary: boards only ever expose `displayName`.
 */

export interface VenuePublic {
    venueId: string;
    /** null => the client renders its own "Unnamed venue" i18n string. */
    name: string | null;
    nameSource: "osm" | "gazetteer" | "votes" | null;
    locality: string | null;
    centroid: { latitude: number; longitude: number };
    radius: number;
    /** Present on /venues/near results only. */
    distanceM?: number;
    entryCount: number;
    playerCount: number;
    /** Set when nameSource === "osm"; the client MUST display it (ODbL). */
    attribution?: string;
}

export interface BoardStandingPublic {
    rank: number;
    displayName: string;
    matchCount: number;
    lastPlayedAt: number;
}

export interface BoardRecordPublic {
    value: number;
    displayName: string;
    date: string;
    epoch: number;
    source: string;
    position: string | null;
}

export interface BoardRecordSetPublic {
    speedMax: BoardRecordPublic | null;
    distanceTotal: BoardRecordPublic | null;
    sprintsTotal: BoardRecordPublic | null;
}

/** One player's best single match at this venue for one metric. Rank is 1-based. */
export interface BoardMetricStandingPublic {
    rank: number;
    displayName: string;
    value: number;
    epoch: number;
}

export interface BoardMetricRankingPublic {
    /** Best first, capped server-side at `BOARD_RANKING_ROWS`. */
    rows: BoardMetricStandingPublic[];
    /** Everyone with a value for this metric in this window, including the untruncated tail. */
    rankedCount: number;
}

export interface BoardRankingSetPublic {
    speedMax: BoardMetricRankingPublic;
    distanceTotal: BoardMetricRankingPublic;
    sprintsTotal: BoardMetricRankingPublic;
}

export interface VenueBoardPublic {
    venueId: string;
    generatedAt: number;
    windowDays: number;
    windowStart: number;
    windowEnd: number;
    localLegend: BoardStandingPublic | null;
    standings: BoardStandingPublic[];
    records: {
        allTime: BoardRecordSetPublic;
        rollingMonth: BoardRecordSetPublic;
    };
    rankings: {
        allTime: BoardRankingSetPublic;
        rollingMonth: BoardRankingSetPublic;
    };
    entryCount: number;
    playerCount: number;
}

interface VenuesNearResponse {
    venues: VenuePublic[];
}

/**
 * Pull the HTTP status off an error without assuming it is an axios error — the request mock
 * used by the unit tests rejects with a bare `{data, status}`.
 */
export function statusOfError(error: any): number | undefined {
    return error?.response?.status ?? error?.status;
}

/** The 202 body: where the server filed this match, and what the write actually did. */
export interface VenueSubmissionAccepted {
    venueId: string;
    pitchId: string | null;
    /** `unchanged` means the resubmit was a no-op and no board was rebuilt. */
    entry: "created" | "updated" | "unchanged";
}

/**
 * What a submission attempt did, from the phone's point of view.
 *
 * `notAccepted` folds together every 204 — not opted in, under 18, no such match, rejected as
 * junk. The server keeps those indistinguishable on purpose and the phone cannot act on the
 * difference either way: in all of them there is no entry and therefore no `venueRef` to write.
 */
export type VenueSubmissionResult =
    | { status: "accepted"; accepted: VenueSubmissionAccepted }
    | { status: "notAccepted" }
    | { status: "retry" };

/**
 * POST /venues/submit/:username.
 *
 * `rev` is the `_rev` of the locally saved match doc. The phone submits the moment the save
 * resolves and push replication is live but not awaited, so the server routinely sees a revision
 * behind — and submitting that one would publish pre-calibration numbers as final. Sending the
 * revision lets the server wait for it and answer `409 {retry: true}` rather than guess.
 */
export async function submitMatchToVenues(
    username: string,
    matchId: string,
    rev?: string
): Promise<VenueSubmissionResult> {
    try {
        const response = await request.post<VenueSubmissionAccepted | null>(
            "/venues/submit/" + encodeURIComponent(username),
            { matchId, rev }
        );
        if (response.status === 202 && response.data?.venueId) {
            return { status: "accepted", accepted: response.data };
        }
        return { status: "notAccepted" };
    } catch (error) {
        if (statusOfError(error) === 409) {
            return { status: "retry" };
        }
        throw error;
    }
}

/** GET /venues/near?lat=&lon= */
export async function getVenuesNear(
    lat: number,
    lon: number
): Promise<VenuePublic[]> {
    const response = await request.get<VenuesNearResponse>(
        "/venues/near?lat=" + lat + "&lon=" + lon
    );
    return response.data?.venues || [];
}

/** GET /venues/:venueId */
export async function getVenue(venueId: string): Promise<VenuePublic> {
    const response = await request.get<VenuePublic>(
        "/venues/" + encodeURIComponent(venueId)
    );
    return response.data;
}

/**
 * GET /venues/:venueId/board — a 404 means "no snapshot has been generated for this venue
 * yet", which is the normal state for a quiet venue. Resolve null so the caller can show the
 * personal half of the page rather than an error.
 */
export async function getVenueBoard(
    venueId: string
): Promise<VenueBoardPublic | null> {
    try {
        const response = await request.get<VenueBoardPublic>(
            "/venues/" + encodeURIComponent(venueId) + "/board"
        );
        return response.data;
    } catch (error) {
        if (statusOfError(error) === 404) {
            return null;
        }
        throw error;
    }
}

/**
 * GET /venues/:venueId/ribbons/:matchId — the signed-in user's ribbons and Local Legend slot for
 * one of their own matches. A 404 means there is nothing to show (not opted in, or no entry for
 * the match at this venue), so it resolves null.
 */
export async function getMatchRibbons(
    venueId: string,
    matchId: string
): Promise<MatchRibbonsPublic | null> {
    try {
        const response = await request.get<MatchRibbonsPublic>(
            "/venues/" +
                encodeURIComponent(venueId) +
                "/ribbons/" +
                encodeURIComponent(matchId)
        );
        return response.data;
    } catch (error) {
        if (statusOfError(error) === 404) {
            return null;
        }
        throw error;
    }
}

/**
 * A match is only worth submitting once it is finished and actually has a GPS track.
 *
 * Training sessions are included on purpose (SCORZA-766). The server rejects them, and
 * submitting one is what tells it to withdraw an entry filed while it was a match. The phone
 * cannot tell whether such an entry exists: the backfill files entries without tagging the
 * match doc, so a missing `venueRef` does not mean "not on a board".
 */
export function isMatchEligibleForVenueSubmission(match?: MatchPhone): boolean {
    return !!(match && match.hasTracking && match.matchFinished);
}

/** How long to wait before the single retry of a `409 {retry: true}`. */
const SUBMIT_RETRY_DELAY_MS = 1000;

/**
 * Submit a match and store the venue it was filed under on the match doc.
 *
 * **When this runs is the whole design** (SCORZA-739 plan §5.1). It is called once the match's
 * stats are FINAL — after the playing-periods screen has been saved, skipped or reset, and after
 * `recalculateStats` has resolved — never from `recalculateStats` itself. The periods decide
 * which GPS points count, which changes distance, sprints, top speed, minutes and, for an
 * uncalibrated match, the centre point the venue is matched on. Submitting earlier writes wrong
 * numbers and lets the drive home with the watch still recording inflate every metric.
 *
 * Awaited by its callers rather than fire-and-forget, because the point is that the ribbons are
 * right when the player looks at the result. A 409 is retried once; anything else gives up
 * silently — the next save/skip, the first view of the result, or the server's own sweep will
 * catch it, and none of that is worth failing a local save over.
 *
 * The `venueRef` is written locally with a normal `updateMatch`. That local write is what
 * re-renders the stats tab; letting the server write it into the user's database instead would
 * race the phone's own writes and lose the tag on conflict.
 */
export async function submitMatchAndTagVenue(
    match?: MatchPhone
): Promise<void> {
    try {
        if (!isMatchEligibleForVenueSubmission(match) || !match?._id) {
            return;
        }

        // Boards are on by default, so this only short-circuits users who have left or are
        // under 18. It is an optimisation, never the enforcement point: the server re-reads the
        // profile and re-applies the age gate on every submission. A training session is let
        // through regardless, because withdrawing its entry must not depend on participation.
        const state = store.getState();
        if (!match.isTraining && !isEligible(state.currentUser?.profile)) {
            return;
        }

        const username = state.auth?.session?.user_id;
        if (!username) {
            return;
        }

        let result = await submitMatchToVenues(username, match._id, match._rev);
        if (result.status === "retry") {
            await new Promise((resolve) =>
                setTimeout(resolve, SUBMIT_RETRY_DELAY_MS)
            );
            result = await submitMatchToVenues(username, match._id, match._rev);
        }

        if (result.status === "notAccepted" && match.isTraining) {
            await untagWithdrawnMatch(match);
            return;
        }

        if (result.status !== "accepted") {
            return;
        }

        await tagMatchWithVenue(match, result.accepted);
    } catch (error) {
        console.log("Venue submission could not be attempted", error);
    }
}

/**
 * The server has withdrawn this training session's entry, so drop the tag that pointed the
 * stats tab at its board, and the cached board that still counts it.
 */
async function untagWithdrawnMatch(match: MatchPhone): Promise<void> {
    if (!match.venueRef) {
        return;
    }
    invalidateVenue(match.venueRef.venueId);
    const updated = copyObject(match);
    delete updated.venueRef;
    await updateMatch(updated);
}

/**
 * Persist the venue the server filed this match under, and drop that venue's cached board so an
 * already-mounted stats tab refetches rather than rendering the board from before this match.
 *
 * The write is skipped when the tag is already current — a no-op resubmit must not cost a
 * revision on a replicating document.
 */
async function tagMatchWithVenue(
    match: MatchPhone,
    accepted: VenueSubmissionAccepted
): Promise<void> {
    invalidateVenue(accepted.venueId);

    const current = match.venueRef;
    if (
        current &&
        current.venueId === accepted.venueId &&
        current.pitchId === accepted.pitchId &&
        current.rev === match._rev
    ) {
        return;
    }

    const updated = copyObject(match);
    updated.venueRef = {
        venueId: accepted.venueId,
        pitchId: accepted.pitchId,
        rev: match._rev || "",
        resolvedAt: Date.now(),
    };
    await updateMatch(updated);
}
